import { useEffect, useState } from 'react';

const VOICE_LEVEL_THRESHOLD = 0.08;

function createAudioContext(): AudioContext {
  const AudioContextCtor =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextCtor) {
    throw new Error('Web Audio API is not available.');
  }
  return new AudioContextCtor();
}

function measureLevel(analyser: AnalyserNode, buffer: Uint8Array<ArrayBuffer>): number {
  analyser.getByteTimeDomainData(buffer);
  let sumSquares = 0;
  for (let i = 0; i < buffer.length; i++) {
    const sample = ((buffer[i] ?? 128) - 128) / 128;
    sumSquares += sample * sample;
  }
  const rms = Math.sqrt(sumSquares / buffer.length);
  return Math.min(1, rms * 4);
}

export function useMicrophoneLevel(stream: MediaStream | null, enabled = true): number {
  const [level, setLevel] = useState(0);

  useEffect(() => {
    if (!stream || !enabled) {
      setLevel(0);
      return;
    }

    const audioTrack = stream.getAudioTracks()[0];
    if (!audioTrack || audioTrack.readyState !== 'live') {
      setLevel(0);
      return;
    }

    let cancelled = false;
    let animationId = 0;
    let audioContext: AudioContext | null = null;

    const start = async () => {
      try {
        audioContext = createAudioContext();
        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }

        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 512;
        analyser.smoothingTimeConstant = 0.7;

        const source = audioContext.createMediaStreamSource(stream);
        source.connect(analyser);

        const buffer = new Uint8Array(analyser.fftSize) as Uint8Array<ArrayBuffer>;

        const tick = () => {
          if (cancelled) return;
          setLevel(measureLevel(analyser, buffer));
          animationId = requestAnimationFrame(tick);
        };

        tick();
      } catch {
        if (!cancelled) setLevel(0);
      }
    };

    void start();

    return () => {
      cancelled = true;
      cancelAnimationFrame(animationId);
      void audioContext?.close();
    };
  }, [stream, enabled]);

  return level;
}

export function isVoiceDetected(level: number): boolean {
  return level >= VOICE_LEVEL_THRESHOLD;
}
